import React, { useState, useEffect, useMemo } from "react";
import { DataTable } from "./ui";
import { MDBContainer, MDBRow, MDBCol, MDBTypography } from "mdb-react-ui-kit";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import OrderStatsCard from "./OrdersReportComponents/OrderStatsCard";
import { api } from "../services";
import { toast } from "react-toastify";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import PriceDisplay from "./PriceDisplay";
import {
  resolveDateLocale,
  formatLocalizedDateTimeParts,
} from "../utils/formatDateTime";
import { getLocalizedPlatformName } from "../utils/platformNames";

const ORDER_REPORT_SORT_FIELDS = {
  platform: "platform",
  orderId: "order_id",
  date: "createdAt",
  status: "status",
  amount: "final_price",
};

function ReportsPage() {
  const { t, i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canExportReport = can(RBAC_MODULES.ORDER_REPORTS, PERMISSION_ACTIONS.EXPORT);
  const noPermissionTip = t("noPermissionToAccessThis");
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    rejectedOrders: 0,
    timeoutOrders: 0,
    completedPercentage: "0",
    cancelledPercentage: "0",
    rejectedPercentage: "0",
    timeoutPercentage: "0",
  });

  const [dateRange, setDateRange] = useState({ dateFrom: "", dateTo: "" });
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [platformOptions, setPlatformOptions] = useState([]);
  const [storesOptions, setStoresOptions] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storesPayload, setStoresPayload] = useState(null);
  const [storesList, setStoresList] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasPrevPage, setHasPrevPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tableSortColumn, setTableSortColumn] = useState("date");
  const [orderSortField, setOrderSortField] = useState("createdAt");
  const [orderSortDirection, setOrderSortDirection] = useState("desc");

  const reportStatuses = [
    { id: "ALL", name: t("all") || "All" },
    { id: "PENDING", name: t("statuses.PENDING") },
    { id: "ACCEPTED", name: t("statuses.ACCEPTED") },
    { id: "READY FOR DELIVERY", name: t("statuses.READY FOR DELIVERY") },
    { id: "OUT FOR DELIVERY", name: t("statuses.OUT FOR DELIVERY") },
    { id: "RIDER ASSIGNED", name: t("statuses.RIDER ASSIGNED") },
    { id: "ACCEPTED BY COURIER", name: t("statuses.ACCEPTED BY COURIER") },
    { id: "PROCESSED", name: t("statuses.PROCESSED") },
    { id: "DELIVERED", name: t("statuses.DELIVERED") },
    { id: "CANCELLED", name: t("statuses.CANCELLED") },
    { id: "REJECTED", name: t("statuses.REJECTED") },
    { id: "TIME OUT", name: t("statuses.TIME OUT") },
    { id: "NOT ABLE TO TAKE ACTION", name: t("statuses.NOT ABLE TO TAKE ACTION") },
    { id: "COMPLETED", name: t("statuses.COMPLETED") },
  ];

  /** Staff default: all accessible stores — only send brandId when user picks a store in this page. */
  const appendStoreBrandFilter = (params) => {
    if (!selectedStoreId) return;
    params.append("brandId", selectedStoreId);
  };

  const buildOrderReportParams = (
    pageArg = 1,
    sortBy = orderSortField,
    sortOrder = orderSortDirection
  ) => {
    const params = new URLSearchParams();
    params.append("page", String(pageArg));
    params.append("limit", String(limit));
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);
    if (selectedCategoryId) params.append("category", selectedCategoryId);
    if (activeTab && activeTab !== "ALL") params.append("status", activeTab);
    if (selectedBranchId) params.append("branch", selectedBranchId);
    appendStoreBrandFilter(params);
    if (debouncedSearch) params.append("search", debouncedSearch);
    if (selectedPlatform) params.append("platform", selectedPlatform);
    if (dateRange.dateFrom) params.append("dateFrom", dateRange.dateFrom);
    if (dateRange.dateTo) params.append("dateTo", dateRange.dateTo);
    return params;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedCategorySearch((categorySearch || "").trim());
    }, 700);
    return () => clearTimeout(h);
  }, [categorySearch]);

  useEffect(() => {
    fetchOrders(page);
  }, [
    debouncedSearch,
    dateRange,
    selectedPlatform,
    selectedBranchId,
    selectedCategoryId,
    activeTab,
    selectedStoreId,
    page,
    limit,
    orderSortField,
    orderSortDirection,
  ]);

  const fetchOrders = async (
    pageArg = 1,
    sortBy = orderSortField,
    sortOrder = orderSortDirection
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = buildOrderReportParams(pageArg, sortBy, sortOrder);

      console.log("Fetching orders with params:", params.toString());

      const response = await api.get(`/order-reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const items = response.data?.orders || [];
      setOrders(items);
      setSelectedIds([]);
      if (response.data?.summary) setSummary(response.data.summary);

      const pag =
        response.data?.pagination || response.data?.data?.pagination || null;
      if (pag) {
        setPage(Number(pag.currentPage) || pageArg);
        setLimit(Number(pag.itemsPerPage) || limit);
        setTotal(Number(pag.totalItems) || items.length);
        setTotalPages(
          Number(pag.totalPages) ||
            Math.max(
              1,
              Math.ceil(
                (Number(pag.totalItems) || items.length) /
                  (Number(pag.itemsPerPage) || limit)
              )
            )
        );
        setHasPrevPage(Boolean(pag.hasPrevPage));
        setHasNextPage(Boolean(pag.hasNextPage));
      } else {
        const totalItems =
          response.data?.totalItems ??
          response.data?.total ??
          response.data?.data?.total ??
          items.length;
        const totalPagesResp =
          response.data?.totalPages ??
          response.data?.data?.totalPages ??
          Math.max(1, Math.ceil((totalItems || items.length) / limit));
        setTotal(totalItems || items.length);
        setTotalPages(totalPagesResp);
        setPage(pageArg);
        setHasPrevPage(null);
        setHasNextPage(null);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setLoading(false);
      // Fallback for demo purposes if API fails or doesn't exist yet
      setOrders([]);
      setSelectedIds([]);
      setSummary({
        totalOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        rejectedOrders: 0,
        timeoutOrders: 0,
        completedPercentage: "0",
        cancelledPercentage: "0",
        rejectedPercentage: "0",
        timeoutPercentage: "0",
      });
    }
  };

  useEffect(() => {
    fetchStores();
    fetchPlatforms();
  }, []);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoresPayload(response.data);
      const stores = response.data?.stores || [];
      setStoresList(stores);
      const options = stores.map((s) => ({
        _id: s?._id,
        name: {
          en: s?.name || s?.name_en || s?.displayName || "",
          ar: s?.name_ar || s?.displayNameAr || s?.name || "",
        },
      }));
      setStoresOptions(options);
    } catch (err) {
      setStoresOptions([]);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/platforms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.success && response.data?.data?.platforms) {
        const options = response.data.data.platforms.map((p) => ({
          _id: p.key,
          name: { en: p.nameEn || p.name, ar: p.nameAr || p.name },
        }));
        setPlatformOptions(options);
      }
    } catch (err) {
      console.error("Error fetching platforms:", err);
      setPlatformOptions([]);
    }
  };

  const updateBranchesForStore = (storeId) => {
    const store = storesList.find((s) => s?._id === storeId);
    const branches = store?.branches || [];
    const options = branches.map((b) => ({
      _id: b?._id,
      name: {
        en: b?.name?.en || b?.name || b?.displayName || "",
        ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      },
    }));
    setBranchesOptions(options);
  };

  const fetchCategoriesForStore = async (storeId, searchTerm = "") => {
    if (!storeId) {
      setCategoriesOptions([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "100");
      params.set("storeId", storeId);
      if ((searchTerm || "").trim())
        params.set("search", (searchTerm || "").trim());

      const response = await api.get(`/categories?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cats = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data?.data?.categories)
        ? response.data.data.categories
        : [];

      const options = cats.map((c) => ({
        _id: c?._id,
        name: {
          en: c?.name?.en || c?.name || c?.displayName || "",
          ar: c?.name?.ar || c?.name_ar || c?.displayNameAr || c?.name || "",
        },
      }));
      setCategoriesOptions(options);
    } catch (err) {
      console.error(
        "Error fetching categories:",
        err.response?.data || err.message
      );
      setCategoriesOptions([]);
    }
  };

  useEffect(() => {
    if (!selectedStoreId) return;
    fetchCategoriesForStore(selectedStoreId, debouncedCategorySearch);
  }, [selectedStoreId, debouncedCategorySearch]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Pending":
        return "warning";
      case "Cancelled":
        return "danger";
      case "Timeout":
        return "secondary";
      default:
        return "primary";
    }
  };

  const formatPlatform = (value) => getLocalizedPlatformName(value, t);

  const formatDateTime = (value) =>
    formatLocalizedDateTimeParts(value, dateLocale, { withSeconds: true });

  const applyTableSort = (column, direction) => {
    const apiField = ORDER_REPORT_SORT_FIELDS[column];
    if (!apiField) return;

    setTableSortColumn(column);
    setOrderSortField(apiField);
    setOrderSortDirection(direction);
    setPage(1);
  };

  const orderColumns = useMemo(
    () => [
      {
        id: "platform",
        header: t("Reports.TableHeaders.platform"),
        strong: true,
        sortable: true,
        render: (order) => formatPlatform(order.platform),
      },
      {
        id: "storeBranch",
        header: t("Reports.TableHeaders.storeAndBranch"),
        render: (order) =>
          order.store && typeof order.store === "object" ? (
            <div className="d-flex flex-column">
              <span style={{ color: "#030F1B" }}>
                {order.store.name?.en || order.store.name || ""}
              </span>
              {order.branch && (
                <span style={{ color: "#030F1B" }}>
                  {typeof order.branch === "object"
                    ? order.branch.name?.en || order.branch.name
                    : order.branch}
                </span>
              )}
            </div>
          ) : (
            order.store
          ),
      },
      {
        id: "orderId",
        header: t("Reports.TableHeaders.ordersId"),
        sortable: true,
        render: (order) => (
          <>
            <div style={{ color: "#030F1B" }}>{order.order_id}</div>
            <div style={{ color: "#030F1B" }}>
              <span className="fw-bold">{t("External ID")}:</span> {order.jahez_id}
            </div>
          </>
        ),
      },
      {
        id: "date",
        header: t("Reports.TableHeaders.date"),
        sortable: true,
        render: (order) => {
          const dt = formatDateTime(order.createdAt);
          return (
            <div className="d-flex flex-column">
              <span>{dt.date}</span>
              <span>{dt.time}</span>
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("Reports.TableHeaders.status"),
        sortable: true,
        render: (order) => {
          const id = (order.status || "").toString().toUpperCase();
          const m = reportStatuses.find((s) => s.id === id);
          return <div style={{ color: "#030F1B" }}>{m?.name || order.status || "-"}</div>;
        },
      },
      {
        id: "amount",
        header: t("Reports.TableHeaders.amount"),
        sortable: true,
        render: (order) => (
          <PriceDisplay value={order.price} iconSize={11} color="#030F1B" />
        ),
      },
    ],
    [t, reportStatuses, dateLocale]
  );

  const handleExport = async () => {
    if (!canExportReport) {
      toast.error(noPermissionTip);
      return;
    }
    try {
      setIsExporting(true);
      const params = new URLSearchParams();
      if (selectedCategoryId) params.append("category", selectedCategoryId);
      if (activeTab && activeTab !== "ALL") params.append("status", activeTab);
      if (selectedBranchId) params.append("branch", selectedBranchId);
      appendStoreBrandFilter(params);
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedPlatform) params.append("platform", selectedPlatform);
      if (dateRange.dateFrom) params.append("dateFrom", dateRange.dateFrom);
      if (dateRange.dateTo) params.append("dateTo", dateRange.dateTo);

      const res = await api.get(`/order-reports/export?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      let filename = "order-reports.csv";
      const cd = res.headers && res.headers["content-disposition"];
      if (cd) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
        if (match && match[1]) filename = match[1].replace(/['"]/g, "");
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting reports:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const exportPrimaryAction = canExportReport
    ? {
        label: isExporting
          ? isArabic
            ? "جاري التصدير"
            : "Exporting"
          : isArabic
          ? "تصدير"
          : "Export",
        onClick: handleExport,
        disabled: isExporting,
        icon: (
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            style={{ transform: isArabic ? "scaleX(-1)" : "none" }}
          />
        ),
      }
    : undefined;

  return (
    <div className="reports-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">
          {t("Reports.ordersReports")}
        </h1>

        <div className="oh-page-section">
          <div className="oh-kpi-row oh-kpi-row--5">
              <OrderStatsCard
                title={t("Reports.totalOrders")}
                value={summary.totalOrders}
                loading={loading}
              />
              <OrderStatsCard
                title={t("Reports.completedOrders")}
                value={summary.completedOrders}
                percentage={summary.completedPercentage}
                loading={loading}
              />
              <OrderStatsCard
                title={t("Reports.cancelledOrders")}
                value={summary.cancelledOrders}
                percentage={summary.cancelledPercentage}
                loading={loading}
              />
              <OrderStatsCard
                title={t("Reports.rejectedOrders")}
                value={summary.rejectedOrders}
                percentage={summary.rejectedPercentage}
                loading={loading}
              />
              <OrderStatsCard
                title={t("Reports.timeoutOrders")}
                value={summary.timeoutOrders}
                percentage={summary.timeoutPercentage}
                loading={loading}
              />
          </div>
        </div>

        <div className="oh-page-section">
          <Toolbar
            toolbarVariant="reports"
            hideIcons={true}
            showDateRange={true}
            dateFrom={dateRange.dateFrom}
            onDateFromChange={(e) => {
              setDateRange((prev) => ({ ...prev, dateFrom: e.target.value }));
              setPage(1);
            }}
            dateTo={dateRange.dateTo}
            onDateToChange={(e) => {
              setDateRange((prev) => ({ ...prev, dateTo: e.target.value }));
              setPage(1);
            }}
            extraDropdowns={[
              {
                label: t("status"),
                placeholder: t("status"),
                options: reportStatuses.map((s) => ({
                  _id: s.id,
                  name: s.name,
                })),
                selectedOption: activeTab || "ALL",
                showClear: false,
                handleSelect: (opt) => {
                  setActiveTab(opt?._id || "ALL");
                  setPage(1);
                },
              },
              {
                label: t("Reports.brand"),
                placeholder: t("Reports.brand"),
                options: storesOptions,
                selectedOption: selectedStoreId || null,
                handleSelect: (opt) => {
                  const id = opt?._id || "";
                  setSelectedStoreId(id);
                  updateBranchesForStore(id);
                  setSelectedBranchId("");
                  fetchCategoriesForStore(id, "");
                  setSelectedCategoryId("");
                  setPage(1);
                },
              },
              {
                label: t("Reports.branch"),
                placeholder: t("Reports.branch"),
                options: branchesOptions,
                selectedOption: selectedBranchId || null,
                disabled: false,
                emptyMessage: !selectedStoreId
                  ? t("Reports.selectBrandFirst")
                  : t("Reports.noOptionsFound"),
                handleSelect: (opt) => {
                  const id = opt?._id || "";
                  setSelectedBranchId(id);
                  setPage(1);
                },
              },
              {
                label: t("Reports.category"),
                placeholder: t("Reports.category"),
                options: categoriesOptions,
                selectedOption: selectedCategoryId || null,
                disabled: false,
                emptyMessage:
                  !selectedStoreId || !selectedBranchId
                    ? t("Reports.selectBrandFirst")
                    : t("Reports.noOptionsFound"),
                onSearchChange: (term) => setCategorySearch(term),
                handleSelect: (opt) => {
                  const id = opt?._id || "";
                  setSelectedCategoryId(id);
                  setPage(1);
                },
              },
              {
                label: t("Reports.platform"),
                placeholder: t("Reports.platform"),
                options: platformOptions,
                selectedOption: selectedPlatform || null,
                handleSelect: (opt) => {
                  const id = (opt?._id || "").toUpperCase();
                  setSelectedPlatform(id);
                  setPage(1);
                },
              },
            ]}
          />
        </div>

        <div className="oh-page-section">
      <DataTable
        tableId="order-reports"
        title={t("orders")}
        columns={orderColumns}
        data={orders}
        rowKey={(row) => row.id || row._id || row.order_id}
        loading={loading}
        emptyState={t("No Data Available")}
        isRtl={isArabic}
        selectable
        selectionMode="checkbox"
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkBarPlacement="end"
        selectedLabel={t("Selected", { defaultValue: "Selected" })}
        selectAllLabel={t("Select All", { defaultValue: "Select All" })}
        primaryAction={exportPrimaryAction}
        sort={{
          column: tableSortColumn,
          direction: orderSortDirection,
          onChange: ({ column, direction }) =>
            applyTableSort(column, direction),
        }}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setLimit(size);
            setPage(1);
          },
          showLabel: t("show"),
          ofLabel: t("of"),
          resultsLabel: t("results", { defaultValue: "results" }),
          isRtl: isArabic,
          previousLabel: t("previous"),
          nextLabel: t("next"),
        }}
      />
        </div>
      </MDBContainer>
    </div>
  );
}

export default ReportsPage;